import { COLS, type Row } from './types.ts';

/**
 * A decoded display cell. `glyph` is the 7-bit code to draw (the held
 * mosaic when hold is active, otherwise the transmitted character, or
 * 0x20 for an attribute cell). `mosaic` says whether to draw it from
 * sixel bits; `sep` applies to mosaics only.
 */
export interface Cell {
  col: number;
  code: number;
  glyph: number;
  fg: number;
  bg: number;
  mosaic: boolean;
  sep: boolean;
  flash: boolean;
  dh: boolean;
  conceal: boolean;
  box: boolean;
  /** This cell is the lower half of a double-height character on the row above. */
  lower: boolean;
}

export interface DecodedRow {
  row: number;
  cells: Cell[];
  /** Row data was ignored because the row above contains double height. */
  suppressed: boolean;
  /** Some cell on this row is double height (so the next row is its lower half). */
  hasDoubleHeight: boolean;
}

/**
 * Level 1 decoder for one row (EN 300 706 §12.2). Attributes are set-at
 * or set-after per table 26; every attribute cell displays as a space
 * unless Hold Mosaics substitutes the held mosaic character.
 */
export function decodeRow(codes: Row, rowIndex = 0): Cell[] {
  let fg = 7, bg = 0;
  let mosaic = false, sep = false, flash = false, dh = false, conceal = false, box = false, hold = false;
  let held = 0x20, heldSep = false;
  const cells: Cell[] = new Array(COLS);

  for (let x = 0; x < COLS; x++) {
    const c = (codes[x] ?? 0x20) & 0x7f;
    if (c < 0x20) {
      let after: (() => void) | undefined;
      if (c <= 0x07) {
        after = () => { fg = c; conceal = false; if (mosaic) { mosaic = false; held = 0x20; } };
      } else if (c === 0x08) {
        after = () => { flash = true; };
      } else if (c === 0x09) {
        flash = false;
      } else if (c === 0x0a) {
        after = () => { box = false; };
      } else if (c === 0x0b) {
        after = () => { box = true; };
      } else if (c === 0x0c) {
        if (dh) { dh = false; held = 0x20; }
      } else if (c === 0x0d) {
        after = () => { if (!dh) { dh = true; held = 0x20; } };
      } else if (c >= 0x10 && c <= 0x17) {
        after = () => { fg = c - 0x10; conceal = false; if (!mosaic) { mosaic = true; held = 0x20; } };
      } else if (c === 0x18) {
        conceal = true;
      } else if (c === 0x19) {
        sep = false;
      } else if (c === 0x1a) {
        sep = true;
      } else if (c === 0x1c) {
        bg = 0;
      } else if (c === 0x1d) {
        bg = fg;
      } else if (c === 0x1e) {
        hold = true;
      } else if (c === 0x1f) {
        after = () => { hold = false; };
      }
      // 0x0e/0x0f (double width/size) and 0x1b (ESC) have no Level 1 effect.
      const useHeld = hold && mosaic && held !== 0x20;
      cells[x] = {
        col: x, code: c, glyph: useHeld ? held : 0x20, fg, bg,
        mosaic: useHeld, sep: useHeld ? heldSep : sep, flash, dh, conceal, box, lower: false,
      };
      if (after) after();
    } else {
      // In mosaics mode, only codes with bit 6 set are G1 mosaics; 0x40–0x5F stay alphanumeric.
      const isMos = mosaic && (c & 0x20) !== 0;
      if (isMos) { held = c; heldSep = sep; }
      cells[x] = { col: x, code: c, glyph: c, fg, bg, mosaic: isMos, sep, flash, dh, conceal, box, lower: false };
    }
  }
  void rowIndex;
  return cells;
}

/**
 * Decode a whole 25-row grid, applying the double-height rule: the row
 * below a row containing double height shows the lower halves of those
 * characters, the local background colour under normal-height cells,
 * and ignores its own transmitted data.
 */
export function decodePage(rows: Row[]): DecodedRow[] {
  const out: DecodedRow[] = [];
  let suppressNext = false;
  for (let r = 0; r < rows.length; r++) {
    if (suppressNext) {
      const upper = out[r - 1]!;
      const cells = upper.cells.map((u): Cell =>
        u.dh
          ? { ...u, lower: true }
          : { col: u.col, code: 0x20, glyph: 0x20, fg: u.fg, bg: u.bg, mosaic: false, sep: false, flash: false, dh: false, conceal: false, box: u.box, lower: false },
      );
      out.push({ row: r, cells, suppressed: true, hasDoubleHeight: false });
      suppressNext = false;
      continue;
    }
    const cells = decodeRow(rows[r]!, r);
    const hasDoubleHeight = cells.some((c) => c.dh);
    out.push({ row: r, cells, suppressed: false, hasDoubleHeight });
    suppressNext = hasDoubleHeight;
  }
  return out;
}

/**
 * Sixel bits for a G1 mosaic code: bit 0 top-left, 1 top-right, 2 middle-left,
 * 3 middle-right, 4 bottom-left, 6 bottom-right (bit 5 is the G1 marker).
 * Returns six booleans in reading order TL, TR, ML, MR, BL, BR.
 */
export function sixels(code: number): [boolean, boolean, boolean, boolean, boolean, boolean] {
  return [
    (code & 0x01) !== 0, (code & 0x02) !== 0,
    (code & 0x04) !== 0, (code & 0x08) !== 0,
    (code & 0x10) !== 0, (code & 0x40) !== 0,
  ];
}
